import React, {useEffect, useMemo, useRef} from 'react';
import {Animated, StyleSheet, Text, View} from 'react-native';
import {MaterialCommunityIcons} from '@expo/vector-icons';

const TransportStatusBadge = ({transport = 'polling', compact = false}) => {
  const normalizedTransport = String(transport || 'polling').toLowerCase();
  const pulseScale = useRef(new Animated.Value(1)).current;
  const pulseOpacity = useRef(new Animated.Value(1)).current;
  const previousTransportRef = useRef(normalizedTransport);

  const config = useMemo(() => {
    if (normalizedTransport === 'mqtt') {
      return {
        label: compact ? 'MQTT' : 'Realtime: MQTT',
        icon: 'access-point-network',
        containerStyle: styles.containerMqtt,
        dotStyle: styles.dotMqtt,
        textStyle: styles.textMqtt,
      };
    }

    if (normalizedTransport === 'socket.io') {
      return {
        label: compact ? 'Socket.IO' : 'Realtime: Socket.IO',
        icon: 'lan-connect',
        containerStyle: styles.containerSocket,
        dotStyle: styles.dotSocket,
        textStyle: styles.textSocket,
      };
    }

    return {
      label: compact ? 'Polling 10s' : 'Realtime: Polling 10s',
      icon: 'sync-alert',
      containerStyle: styles.containerPolling,
      dotStyle: styles.dotPolling,
      textStyle: styles.textPolling,
    };
  }, [compact, normalizedTransport]);

  useEffect(() => {
    const previousTransport = previousTransportRef.current;

    if (previousTransport === normalizedTransport) {
      return;
    }

    previousTransportRef.current = normalizedTransport;

    Animated.sequence([
      Animated.parallel([
        Animated.timing(pulseScale, {
          toValue: 1.08,
          duration: 130,
          useNativeDriver: true,
        }),
        Animated.timing(pulseOpacity, {
          toValue: 0.72,
          duration: 130,
          useNativeDriver: true,
        }),
      ]),
      Animated.parallel([
        Animated.timing(pulseScale, {
          toValue: 1,
          duration: 160,
          useNativeDriver: true,
        }),
        Animated.timing(pulseOpacity, {
          toValue: 1,
          duration: 160,
          useNativeDriver: true,
        }),
      ]),
    ]).start();
  }, [normalizedTransport, pulseOpacity, pulseScale]);

  return (
    <Animated.View
      style={[
        styles.container,
        config.containerStyle,
        {
          transform: [{scale: pulseScale}],
          opacity: pulseOpacity,
        },
      ]}
    >
      <View style={[styles.dot, config.dotStyle]} />
      <MaterialCommunityIcons name={config.icon} size={14} color={config.textStyle.color} />
      <Text style={[styles.text, config.textStyle]}>{config.label}</Text>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 6,
    gap: 6,
  },
  containerMqtt: {
    backgroundColor: '#ecfdf5',
  },
  containerSocket: {
    backgroundColor: '#eff6ff',
  },
  containerPolling: {
    backgroundColor: '#fff7ed',
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 999,
  },
  dotMqtt: {
    backgroundColor: '#16a34a',
  },
  dotSocket: {
    backgroundColor: '#2563eb',
  },
  dotPolling: {
    backgroundColor: '#ea580c',
  },
  text: {
    fontSize: 12,
    fontWeight: '700',
  },
  textMqtt: {
    color: '#166534',
  },
  textSocket: {
    color: '#1d4ed8',
  },
  textPolling: {
    color: '#9a3412',
  },
});

export default React.memo(TransportStatusBadge);

